import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { Message } from '@dev-streetbeat/chat';

import PortfolioConfigData from 'config/portfolio-config.json';
import { bodyEmail } from 'data/body-email';
import { useToast } from 'shared/components/ui/use-toast';
import { AssistantService } from 'shared/services/AssistantService';
import chatStore from 'shared/store/chatStore';
import useThreadsStore from 'shared/store/threadsStore';
import { createAssistantMessage } from 'utils/assistantUtils';
import { extractPortfolioFromArtifact, isPortfolioStoryArtifact } from 'utils/portfolioUtils';
import { FinecoPortfolioStoryArtifact } from '../../fineco-portfolio-story-artifact/FinecoPortfolioStoryArtifact';
import { FinecoPortfolioButtons } from './components/FinecoPortfolioButtons';
import FinecoPortfolioTitle from '../components/FinecoPortfolioTitle';
import { Section } from '../components/Section';
import AnalisiRischioRendimentoAttesoContainer from '../components/analisi-rischio-e-rendimento-atteso/AnalisiRischioRendimentoAttesoContainer';
import AnalisiStrumentiContainer from '../components/analisi-strumenti/AnalisiStrumentiContainer';
import AssetAllocationImplicitaContainer from '../components/asset-allocation-implicita/AssetAllocationImplicitaContainer';
import CaratteristicheDiPortafoglioContainer from '../components/caratteristiche-di-portafoglio/CaratteristicheDiPortafoglioContainer';
import CommentoContainer from '../components/commento/CommentoContainer';
import ComposizioneDelPortafoglioContainer from '../components/composizione-del-portafoglio/ComposizioneDelPortafoglioContainer';
import ComposizioneDelPortafoglioImportedContainer from '../components/composizione-del-portafoglio/ComposizioneDelPortafoglioImportedContainer';
import DistribuzioneTipologiaContainer from '../components/DistribuzioneTipologiaContainer';
import DistribuzioneDeiProventiContainer from '../components/distribuzione-proventi/DistribuzioneDeiProventiContainer';
import NegoziabilitaFinecoContainer from '../components/negoziabilita-fineco/NegoziabilitaFinecoContainer';
import EfficientFrontierContainer from '../components/frontiera-efficiente/EfficientFrontierContainer';
import IndicatoriFinanziariContainer from '../components/indicatori-finanziari/IndicatoriFinanziariContainer';
import ScomposizionePortafoglioContainer from '../components/scomposizione-di-portafoglio/ScomposizionePortafolioContainer';
import DistribuzioneEsgContainer from '../components/sostenibilita/components/DistribuzioneEsgContainer';
import { ef_data } from '../FinecoConfigData';
import BacktestContainer from '../components/backtest/BacktestContainer';
import { createLogger } from 'shared/logger';
import { saveErrorMessage } from './saveErrorMessage';

import styles from 'pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss';

const log = createLogger('FinecoPortfolio');

type Props = {
  artifact: any;
  message: Message & { thread_id: string };
  finecoPortfolioAndPortfolioStoryTogether: boolean;
  onLoaded?: () => void;
};

// Memoize portfolio story component to prevent unnecessary re-renders
const MemoizedPortfolioStory = memo(
  ({ message, onLoaded }: { message: Message & { thread_id: string }; onLoaded?: () => void }) => (
    <>
      {message.artifacts?.map((artifact: any) => {
        if (isPortfolioStoryArtifact(artifact)) {
          return (
            <FinecoPortfolioStoryArtifact key={Math.random()} data={artifact.custom_data.data} onLoaded={onLoaded} />
          );
        }
        return null;
      })}
    </>
  ),
);

MemoizedPortfolioStory.displayName = 'MemoizedPortfolioStory';

export const FinecoPortfolio: React.FC<Props> = memo(
  ({ artifact, message, onLoaded }) => {
    const appendMessages = useThreadsStore((state) => state.appendMessages);
    const { t } = useTranslation();
    const { toast } = useToast();
    const params = useParams();
    const threadId = params.threadId;
    const [reportData, setReportData] = useState<any>(null);
    const [generatingPdf, setGeneratingPdf] = useState<boolean>(false);
    const [generatingDownloadPdf, setGeneratingDownloadPdf] = useState<boolean>(false);
    const [generatingExcel, setGeneratingExcel] = useState<boolean>(false);
    // Local state for immediate UI feedback after successful actions
    const [justImported, setJustImported] = useState<boolean>(false);
    const [justSaved, setJustSaved] = useState<boolean>(false);
    // Date range state
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [backtestLoading, setBacktestLoading] = useState<boolean>(false);

    const generateReportRef = useRef(null);

    // Extract data using shared helper
    const {
      portfolioData,
      portfolioId,
      commentoAlPortafoglio,
      isFinecoImportedPortfolio,
      canImportToXnet,
      isOptimizedPortfolio,
    } = extractPortfolioFromArtifact(artifact);

    // Portfolio actions state from shared Zustand store (single source of truth)
    const portfolioActionsMetadata = chatStore((s) => s.portfolioActionsState[threadId ?? '']) as
      | {
          latest_portfolio_id?: string;
          portfolios?: Record<string, { imported_to_xnet?: boolean; saved_to_my_portfolios?: boolean }>;
        }
      | undefined;

    useEffect(() => {
      if (!threadId) return;
      AssistantService.getPortfolioActions(threadId)
        .then((data) => {
          if (data && Object.keys(data).length > 0) {
            chatStore.getState().setPortfolioActionsState(threadId, data);
          }
        })
        .catch((error) => log.error('fetch portfolio actions state failed', { threadId, error }));
    }, [threadId]);
    // In builder mode (no portfolioActionsMetadata), buttons stay enabled — no state tracking
    const isLatestPortfolio =
      !portfolioActionsMetadata?.latest_portfolio_id || portfolioActionsMetadata.latest_portfolio_id === portfolioId;
    const alreadyImported = portfolioActionsMetadata
      ? justImported || portfolioActionsMetadata.portfolios?.[portfolioId ?? '']?.imported_to_xnet === true
      : false;
    const alreadySaved = portfolioActionsMetadata
      ? justSaved || portfolioActionsMetadata.portfolios?.[portfolioId ?? '']?.saved_to_my_portfolios === true
      : false;
    const effectiveCanImportToXnet = canImportToXnet && isLatestPortfolio && !alreadyImported;
    const effectiveCanSavePortfolio = isLatestPortfolio && !alreadySaved;

    // Memoize Excel generation callback
    const requestGenerateExcel = useCallback(async () => {
      setGeneratingExcel(true);
      try {
        let response;

        // Show toast for download start
        toast({
          title: t('generateReport.downloading'),
          description: t('generateReport.preparingExcel'),
        });

        // Prefer v2 API with portfolio data, fallback to v1 with thread/portfolio IDs
        if (portfolioData) {
          response = await AssistantService.createExcel(portfolioId);
        } else {
          throw new Error('No portfolio data or thread/portfolio IDs available for Excel generation');
        }

        const filename = `portfolio_${portfolioId || 'report'}.xlsx`;
        // response is now a Blob from RestService.download
        const blob =
          response instanceof Blob
            ? response
            : new Blob([response], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);

        // Show success toast
        toast({
          title: t('generateReport.downloadComplete'),
          description: t('generateReport.excelReady'),
        });

        // Add assistant message if in thread context
        if (threadId) {
          const assistantMessage = createAssistantMessage(t('assistant.excelDownloaded'), threadId);
          appendMessages(threadId, [assistantMessage]);
        }
      } catch (error) {
        log.error('Excel generation failed', { portfolioId, threadId, error });
        // Show error toast
        toast({
          title: t('generateReport.downloadError'),
          description: t('generateReport.excelError'),
          variant: 'destructive',
        });

        // Add assistant message if in thread context
        if (threadId) {
          const assistantMessage = createAssistantMessage(t('assistant.excelDownloadedError'), threadId);
          appendMessages(threadId, [assistantMessage]);
        }
      } finally {
        setGeneratingExcel(false);
      }
    }, [threadId, portfolioId, portfolioData, appendMessages, t, toast]);

    const sectionsConfig = useMemo(
      () => ({
        efficientFrontierContainer: {
          label: '',
          component: (
            <EfficientFrontierContainer ef_data={ef_data} reportData={reportData} isOptimized={isOptimizedPortfolio} />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        statsContainer: {
          label: '',
          component: (
            <CaratteristicheDiPortafoglioContainer data={portfolioData} label={PortfolioConfigData.stats.label} />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        indicatoriFinanziariContainer: {
          label: '',
          component: (
            <IndicatoriFinanziariContainer
              reportData={reportData}
              label={PortfolioConfigData.indicatori_finanziari.label}
            />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        distribuzioneTipologiaStrumentiContainer: {
          label: '',
          component: <DistribuzioneTipologiaContainer reportData={reportData} />,
          isOpen: true,
          showFoldingTitle: false,
        },
        portfolio: {
          label: PortfolioConfigData.portfolio.label,
          component: isFinecoImportedPortfolio ? (
            <ComposizioneDelPortafoglioImportedContainer
              reportData={reportData}
              portfolioData={portfolioData}
              requestGenerateExcel={requestGenerateExcel}
              generatingExcel={generatingExcel}
            />
          ) : (
            <ComposizioneDelPortafoglioContainer
              reportData={reportData}
              requestGenerateExcel={requestGenerateExcel}
              generatingExcel={generatingExcel}
            />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        ...(reportData?.negoziabilita?.length > 0 && {
          negoziabilitaFineco: {
            label: 'Negoziabilità Fineco',
            component: <NegoziabilitaFinecoContainer reportData={reportData} />,
            isOpen: false,
            showFoldingTitle: true,
          },
        }),
        // Insurance guided portfolios: no «Distribuzione dei proventi» section.
        ...(portfolioData?.is_insurance_portfolio !== true && {
          caratteristicaDiPortafoglio: {
            label: PortfolioConfigData.caratteristiche_di_portafoglio.label,
            component: <DistribuzioneDeiProventiContainer reportData={reportData} />,
            isOpen: false,
            showFoldingTitle: true,
          },
        }),
        assetAllocationImplicita: {
          label: PortfolioConfigData.implicit_asset_allocation.label,
          component: <AssetAllocationImplicitaContainer reportData={reportData} />,
          isOpen: false,
          showFoldingTitle: true,
        },
        scomposizionePortafoglio: {
          label: PortfolioConfigData.scomposizione_portafoglio.label,
          component: <ScomposizionePortafoglioContainer reportData={reportData} />,
          isOpen: false,
          showFoldingTitle: true,
        },
        analisi_rischio_rendimento_atteso: {
          label: PortfolioConfigData.analisi_rischio_rendimento_atteso.label,
          component: <AnalisiRischioRendimentoAttesoContainer reportData={reportData} />,
          isOpen: false,
          showFoldingTitle: true,
        },
        ...(reportData?.analisi_backtest?.simulazione_andamento_portafoglio?.length > 0 && {
          backtestContainer: {
            label: PortfolioConfigData.backtest.label,
            component: (
              <BacktestContainer
                reportData={reportData}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                backtestLoading={backtestLoading}
              />
            ),
            isOpen: false,
            showFoldingTitle: true,
          },
        }),
        analisi_strumenti: {
          label: PortfolioConfigData.analisi_strumenti.label,
          component: <AnalisiStrumentiContainer reportData={reportData} />,
          isOpen: false,
          showFoldingTitle: true,
        },
        distribuzioneEsg: {
          label: 'Sostenibilità',
          component: <DistribuzioneEsgContainer reportData={reportData} />,
          isOpen: false,
          showFoldingTitle: true,
        },
        ...(commentoAlPortafoglio === true && {
          portfolioStory: {
            label: 'Commento al portafoglio',
            component: <CommentoContainer threadId={threadId} portfolioId={portfolioId} />,
            isOpen: false,
            showFoldingTitle: true,
          },
        }),
      }),
      [
        reportData,
        artifact,
        message,
        onLoaded,
        ef_data,
        startDate,
        endDate,
        backtestLoading,
        requestGenerateExcel,
        generatingExcel,
        threadId,
        portfolioId,
        commentoAlPortafoglio,
        isFinecoImportedPortfolio,
        isOptimizedPortfolio,
      ],
    );

    const [openSections, setOpenSections] = useState(() =>
      Object.fromEntries(Object.entries(sectionsConfig).map(([key, { isOpen }]) => [key, isOpen])),
    );

    const toggleSection = useCallback((section: string) => {
      setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
    }, []);

    // Memoize PDF generation callback
    const requestGeneratePdf = useCallback(
      async (reportSpec: any, fromReport: boolean = false) => {
        if (fromReport) {
          setGeneratingPdf(true);
        }

        try {
          let rawPdf;

          // Prefer v2 API with portfolio data, fallback to v1 with thread/portfolio IDs
          if (portfolioData) {
            // Show toast for download start
            toast({
              title: t('generateReport.downloading'),
              description: t('generateReport.preparingPdf'),
            });

            // Prepare query parameters for backtest date range
            const queryParams: { backtest_start?: string; backtest_end?: string } = {};

            if (startDate) {
              queryParams.backtest_start = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
            }

            if (endDate) {
              queryParams.backtest_end = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
            }

            rawPdf = await AssistantService.createPdf(
              portfolioId,
              reportSpec,
              Object.keys(queryParams).length > 0 ? queryParams : undefined,
            );
          } else {
            throw new Error('No portfolio data or thread/portfolio IDs available for PDF generation');
          }

          const blob = new Blob([rawPdf], { type: 'application/pdf' });
          if (blob.type === 'application/pdf') {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `portfolio_${portfolioId || 'report'}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Show success toast
            toast({
              title: t('generateReport.downloadComplete'),
              description: t('generateReport.pdfReady'),
            });

            // Add assistant message if in thread context
            if (threadId) {
              const assistantMessage = createAssistantMessage(t('assistant.reportDownloaded'), threadId);
              appendMessages(threadId, [assistantMessage]);
            }
          } else {
            // Show error toast
            toast({
              title: t('generateReport.downloadError'),
              description: t('generateReport.pdfError'),
              variant: 'destructive',
            });

            // Add assistant message if in thread context
            if (threadId) {
              const assistantMessage = createAssistantMessage(t('assistant.reportDownloadedError'), threadId);
              appendMessages(threadId, [assistantMessage]);
            }
          }
        } catch (error) {
          log.error('PDF generation failed', { portfolioId, threadId, error });
          if (threadId) {
            const assistantMessage = createAssistantMessage(t('assistant.reportDownloadedError'), threadId);
            appendMessages(threadId, [assistantMessage]);
          }
        } finally {
          setGeneratingPdf(false);
          setGeneratingDownloadPdf(false);
        }
      },
      [threadId, portfolioId, portfolioData, appendMessages, t, startDate, endDate],
    );

    // Memoize email handler
    const handleEmailClick = useCallback(() => {
      const subject = 'Report di analisi del portafoglio';
      const body = bodyEmail;

      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }, []);

    const handleSavePortfolio = useCallback(() => {
      const savePortfolio = async () => {
        try {
          let data;

          // Prefer v2 API with portfolio data, fallback to v1 with thread/portfolio IDs
          if (portfolioData) {
            // Intent: advisor clicked "Salva nei miei portafogli".
            log.info('saving portfolio to My Portfolios', { portfolioId, threadId });
            data = await AssistantService.saveInMyPortfolio(portfolioId, threadId);
          } else {
            throw new Error('No portfolio data or thread/portfolio IDs available for saving portfolio');
          }

          if (data?.url) {
            log.info('portfolio saved to My Portfolios', { portfolioId, threadId });
            setJustSaved(true);
            window.open(data.url, '_blank');
          } else {
            log.warn('save portfolio returned no url', { portfolioId, threadId });
            toast({
              title: t('global.error'),
              description: t('assistant.portfolioSaveUrlError'),
            });
          }
        } catch (error) {
          log.error('save portfolio failed', { portfolioId, threadId, error });
          toast({
            title: t('global.error'),
            description: saveErrorMessage(error, t('assistant.portfolioSaveUrlError')),
          });
        }
      };

      savePortfolio();
    }, [threadId, portfolioId, portfolioData, toast, t]);

    const handleImportToXnet = useCallback(() => {
      const importToXnet = async () => {
        try {
          if (!portfolioData) {
            throw new Error('No portfolio data or thread/portfolio IDs available for importing to X-Net');
          }

          // Intent: advisor clicked "Importa in X-Net".
          log.info('importing portfolio to X-Net', { portfolioId, threadId });
          const response = await AssistantService.importToXnet(portfolioId, threadId);

          if (response?.status === 'ok') {
            log.info('portfolio imported to X-Net', { portfolioId, threadId });
            setJustImported(true);
            toast({
              title: t('assistant.portfolioImportedToXnetTitle'),
              description: t('assistant.portfolioImportedToXnet'),
            });
          } else {
            log.warn('X-Net import not accepted', {
              portfolioId,
              threadId,
              status: response?.status,
            });
            toast({
              title: t('global.error'),
              description: response?.message || t('assistant.portfolioImportToXnetError'),
            });
          }
        } catch (error) {
          log.error('X-Net import failed', { portfolioId, threadId, error });
          toast({
            title: t('global.error'),
            description: t('assistant.portfolioImportToXnetError'),
          });
        }
      };

      importToXnet();
    }, [threadId, portfolioId, portfolioData, toast, t]);

    // Initial data fetch
    useEffect(() => {
      const fetchData = async () => {
        if (portfolioData) {
          try {
            const data = await AssistantService.fetchReportData(portfolioId);
            setReportData(data);

            // Extract date range from original graph data
            if (data?.analisi_backtest?.simulazione_andamento_portafoglio?.length > 0) {
              const andamentoData = data.analisi_backtest.simulazione_andamento_portafoglio;

              // Sort by date
              const sortedDates = andamentoData
                .map((item) => new Date(item.date))
                .sort((a, b) => a.getTime() - b.getTime());

              // Get first and last date
              if (sortedDates.length > 0) {
                const firstDate = sortedDates[0];
                const lastDate = sortedDates[sortedDates.length - 1];

                // Set the date range
                setStartDate(firstDate);
                setEndDate(lastDate);
              }
            }
          } catch (error) {
            log.error('fetch report data failed', { portfolioId, error });
          }
        }
      };

      fetchData();
      onLoaded?.();
    }, [portfolioData, onLoaded]);

    // Refetch data when dates change (including when cleared)
    const datesInitialized = useRef(false);
    useEffect(() => {
      // Skip the initial render to avoid double-fetching
      if (!datesInitialized.current) {
        datesInitialized.current = true;
        return;
      }

      const fetchDataWithDateRange = async () => {
        if (threadId && portfolioId) {
          try {
            setBacktestLoading(true);

            // Prepare query parameters for backtest date range
            const queryParams: { backtest_start?: string; backtest_end?: string } = {};

            if (startDate) {
              queryParams.backtest_start = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
            }

            if (endDate) {
              queryParams.backtest_end = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
            }

            const data = await AssistantService.fetchReportData(
              portfolioId,
              Object.keys(queryParams).length > 0 ? queryParams : undefined,
            );

            setReportData(data);
          } catch (error) {
            log.error('fetch report data (date range) failed', { portfolioId, threadId, error });
          } finally {
            setBacktestLoading(false);
          }
        }
      };

      // Always fetch when dates change (including when both are cleared)
      fetchDataWithDateRange();
    }, [portfolioData, startDate, endDate]);

    if (!reportData) {
      return <div className={styles.loadingPortfolio}>{t('portfolio.loadingPortfolio')}</div>;
    }

    return (
      <div className={styles.portfolioMainContainer}>
        <div className={styles.portfolioContainer}>
          <div className={styles.portfolioContainerInner}>
            <div className={styles.portfolioContainerInnerInner}>
              <FinecoPortfolioTitle title={t('portfolio.PortfolioTitle')} />

              {/* START sections */}
              {Object.entries(sectionsConfig).map(([sectionKey, { label, component, showFoldingTitle }]) => {
                if (component.props.children) {
                  const shouldRender = React.Children.toArray(component.props.children).some((child) => child !== null);
                  if (!shouldRender) return null;
                }

                return (
                  <div key={sectionKey} className={styles.sectionContainer}>
                    <Section
                      showFoldingTitle={showFoldingTitle}
                      toggleSection={toggleSection}
                      openSections={openSections}
                      reportData={reportData}
                      label={label}
                      section={sectionKey}
                    >
                      <div className={styles.sectionComponentContainer}>{component}</div>
                    </Section>
                  </div>
                );
              })}
              {/* END sections */}
            </div>

            <FinecoPortfolioButtons
              generatingPdf={generatingPdf}
              generatingDownloadPdf={generatingDownloadPdf}
              requestGeneratePdfAction={requestGeneratePdf}
              handleSavePortfolio={handleSavePortfolio}
              handleEmailClick={handleEmailClick}
              handleImportToXnet={canImportToXnet ? handleImportToXnet : undefined}
              setGeneratingDownloadPdf={setGeneratingDownloadPdf}
              generateReportRef={generateReportRef}
              artifact={artifact}
              threadId={threadId}
              portfolioId={portfolioId}
              portfolioData={portfolioData}
              countervalueTotal={reportData?.countervalue?.total}
              commentoAlPortafoglio={commentoAlPortafoglio}
              canImportToXnet={effectiveCanImportToXnet}
              canSavePortfolio={effectiveCanSavePortfolio}
            />
          </div>
        </div>
        <div id={`lastAdvisorMessageFinecoPortfolio${portfolioId}`} className={styles.bottom} />
      </div>
    );
  },
  (prev, next) => prev.artifact === next.artifact && prev.message === next.message && prev.onLoaded === next.onLoaded,
);

FinecoPortfolio.displayName = 'FinecoPortfolioArtifact';

export default FinecoPortfolio;
